import { doc, updateDoc, increment, getDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { loadStripe } from '@stripe/stripe-js';

const stripe = loadStripe('pk_live_51E7r1yE0YzXWF5RLin5px6VCk3PjUYwqfaFcuvs86frdKi9uKhhzTYtWGWY6KxROeIX2Y3r00UbGTw7R5BV09yGr001IEaX7x2');

interface PaymentMethodTestResult {
  success: boolean;
  error?: string;
}

const registerWalletTransaction = async (
  companyId: string,
  paymentIntentId: string
) => {
  const transactionData = {
    amount: 1, // 1€ de teste
    companyId,
    createdAt: new Date().toISOString(),
    description: 'Teste de validação do cartão',
    orderId: `TEST${paymentIntentId}`,
    status: 'completed',
    type: 'deposit'
  };

  await addDoc(collection(db, 'walletTransactions'), transactionData);
};

export const testPaymentMethod = async (
  companyId: string,
  stripeCustomerId: string,
  paymentMethodId: string
): Promise<PaymentMethodTestResult> => {
  try {
    console.log('Iniciando teste de pagamento:', { companyId, stripeCustomerId, paymentMethodId });

    // 1. Primeiro, verificar se a empresa existe e obter dados atuais
    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      throw new Error('Empresa não encontrada');
    }

    const companyData = companySnap.data();
    console.log('Dados da empresa:', companyData);

    // 2. Criar um PaymentIntent de 1€
    const requestData = {
      amount: 100, // 1€ em centavos
      currency: 'eur',
      paymentMethodId,
      customerId: stripeCustomerId === 'cus_test' ? null : stripeCustomerId,
      companyId
    };

    console.log('Enviando requisição para Stripe:', requestData);

    // Obter a URL base da API
    const baseUrl = process.env.NODE_ENV === 'development' 
      ? 'http://localhost:3001'  // URL do servidor Express
      : window.location.origin;  // URL de produção
      
    console.log('URL da API:', `${baseUrl}/api/create-payment-intent`);
    
    const response = await fetch(`${baseUrl}/api/create-payment-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    });

    console.log('Status da resposta:', response.status);
    
    const responseText = await response.text();
    console.log('Resposta do servidor:', responseText);

    if (!response.ok) {
      throw new Error(`Erro na API: ${response.status} - ${responseText}`);
    }

    let result;
    try {
      result = JSON.parse(responseText);
    } catch (e) {
      console.error('Erro ao fazer parse da resposta:', responseText);
      throw new Error('Resposta inválida do servidor');
    }

    if (result.success) {
      console.log('Pagamento bem sucedido:', result);

      // 3. Se o pagamento for bem sucedido, atualizar a wallet e salvar o método de pagamento
      await updateDoc(companyRef, {
        walletBalance: increment(1), // Adiciona 1€
        defaultPaymentMethod: paymentMethodId,
        stripeCustomerId: result.customerId, // Caso tenha sido criado um novo customer
        lastFourDigits: result.lastFourDigits, // Salvando os últimos 4 dígitos
        lastPaymentTest: {
          date: new Date().toISOString(),
          success: true,
          paymentIntentId: result.paymentIntentId
        }
      });

      // 4. Registrar a transação na wallet
      await registerWalletTransaction(companyId, result.paymentIntentId);

      return { success: true };
    } else {
      throw new Error(result.error || 'Pagamento não autorizado');
    }
  } catch (error: any) {
    console.error('Erro no teste de pagamento:', error);

    // Registrar a falha
    const companyRef = doc(db, 'companies', companyId);
    await updateDoc(companyRef, {
      defaultPaymentMethod: null,
      lastPaymentTest: {
        date: new Date().toISOString(),
        success: false,
        error: error.message
      }
    });

    return {
      success: false,
      error: error.message || 'Erro ao processar pagamento de teste'
    };
  }
};
